import React, { useState } from "react";
import { notification } from "antd";
import { FoCard, FoDialog, FoKpis, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { PagerTable, SelectField, TextField, VendorSelect, useOsResource, useVendorOptions, vendorName } from "./osForm";
import { DUE_STATUSES, pageMeta } from "./osRules";

export default function DuePayoutBoard() {
  const vendors = useVendorOptions();
  const [vendorId, setVendorId] = useState("");
  const [status, setStatus] = useState("");
  const [applied, setApplied] = useState({ vendorId: "", status: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [action, setAction] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [cashCycle, setCashCycle] = useState("");
  const [busy, setBusy] = useState(false);
  const view = useOsResource(
    (auth) => os.duePayouts(auth, { ...applied, page, limit }),
    [applied, page, limit]
  );
  const summary = view.data?.summary || {};
  const pack = pageMeta(view.data?.payouts);
  const partial = summary.partiallyPaid || {};
  const full = summary.fullyPaid || {};

  const run = async () => {
    if (!action) return;
    setBusy(true);
    try {
      if (action.type === "pay") {
        if (!(Number(amount) > 0)) {
          notification.error({ message: "Payment amount must be greater than zero." });
          setBusy(false);
          return;
        }
        await os.recordDuePayment(view.token, action.vendorId, { amount: Number(amount), note, cashCycle });
        notification.success({ message: "Payment recorded." });
      } else if (action.type === "date") {
        await os.setDueDate(view.token, action.vendorId, dueDate);
        notification.success({ message: "Due date saved." });
      } else if (action.type === "escalate") {
        await os.escalateDue(view.token, action.vendorId, note);
        notification.success({ message: "Brand escalated." });
      }
      setAction(null);
      view.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const remind = async (vendor) => {
    try {
      await os.remindDue(view.token, vendor);
      notification.success({ message: "Reminder sent." });
      view.reload();
    } catch (err) {
      /* toast already shown */
    }
  };

  const remindAll = async () => {
    setBusy(true);
    try {
      const result = await os.remindAllDue(view.token);
      notification.success({ message: `Reminders sent: ${result?.reminded ?? 0}. Failed: ${result?.failed ?? 0}.` });
      view.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fo-stack">
      <FoKpis
        items={[
          { label: "Total due", value: `${formatEgp(summary.totalDue)} EGP`, note: "Positive wallet dues" },
          { label: "Due today", value: `${formatEgp(summary.dueToday)} EGP` },
          { label: "Due this week", value: `${formatEgp(summary.dueThisWeek)} EGP`, note: "Includes due today" },
          { label: "Overdue", value: `${formatEgp(summary.overdue)} EGP`, hot: true },
          { label: "Partially paid", value: `${formatEgp(partial.amount)} EGP`, note: `${partial.count || 0} brands · remaining due` },
          { label: "Fully paid", value: `${formatEgp(full.amount)} EGP`, note: `${full.count || 0} brands · paid this cycle` },
        ]}
      />
      <p className="fo-note">Due this week already includes amounts due today. Do not add those two cards together. An escalated brand can still be overdue; the badge says Escalated and the overdue flag stays on.</p>
      <div className="fo-tools">
        <VendorSelect vendors={vendors} value={vendorId} onChange={setVendorId} emptyLabel="All brands" />
        <SelectField label="Status" value={status} onChange={setStatus} allowEmpty options={DUE_STATUSES} />
        <button type="button" className="fo-btn" onClick={() => { setPage(1); setApplied({ vendorId, status }); }}>Apply</button>
        <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={remindAll}>Send all reminders</button>
      </div>
      <FoCard title="Due board" note="The Due Payouts screen keeps the existing list and Excel export. Payments here create a Paid payout, the ledger entry, and the payout email.">
        <PagerTable
          loading={view.loading}
          dataSource={pack.docs}
          rowKey={(row) => row.vendor?._id || row.vendorId}
          page={page}
          limit={limit}
          total={pack.total}
          minWidth={1100}
          onChange={(nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); }}
          empty="No due payouts in this view."
          columns={[
            { title: "Brand", dataIndex: "vendor", render: (vendor) => vendorName(vendor) },
            { title: "Status", dataIndex: "status", render: (value) => statusBadge(value) },
            { title: "Due date", dataIndex: "dueDate", render: (value) => value ? String(value).slice(0, 10) : "—" },
            { title: "Total due", dataIndex: "totalDue", render: (value) => `${formatEgp(value)} EGP` },
            { title: "Paid this cycle", dataIndex: "amountPaidThisCycle", render: (value) => formatEgp(value) },
            { title: "Reminders", dataIndex: "reminderCount" },
            {
              title: "",
              key: "actions",
              render: (_, row) => {
                const id = row.vendor?._id || row.vendorId;
                const canPay = Number(row.totalDue) > 0 && !row.fullyPaid;
                return (
                  <div className="fo-tier-acts">
                    {canPay && <button type="button" className="fo-btn fo-btn-primary" onClick={() => { setAmount(""); setNote(""); setCashCycle(""); setAction({ type: "pay", vendorId: id }); }}>Pay</button>}
                    <button type="button" className="fo-btn" onClick={() => remind(id)}>Remind</button>
                    <button type="button" className="fo-btn" onClick={() => { setNote(""); setAction({ type: "escalate", vendorId: id }); }}>Escalate</button>
                    <button type="button" className="fo-btn" onClick={() => { setDueDate(row.dueDate ? String(row.dueDate).slice(0, 10) : ""); setAction({ type: "date", vendorId: id }); }}>Due date</button>
                  </div>
                );
              },
            },
          ]}
        />
      </FoCard>
      <FoDialog open={action?.type === "pay"} title="Record payment" onClose={() => !busy && setAction(null)}>
        <div className="fo-form-grid">
          <TextField label="Amount" type="number" value={amount} onChange={setAmount} />
          <TextField label="Cash cycle" value={cashCycle} onChange={setCashCycle} />
          <TextField label="Note" value={note} onChange={setNote} />
        </div>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={run}>Record payment</button>
        </div>
      </FoDialog>
      <FoDialog open={action?.type === "date"} title="Set due date" onClose={() => !busy && setAction(null)}>
        <TextField label="Due date" type="date" value={dueDate} onChange={setDueDate} />
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy || !dueDate} onClick={run}>Save date</button>
        </div>
      </FoDialog>
      <FoDialog open={action?.type === "escalate"} title="Escalate brand" onClose={() => !busy && setAction(null)}>
        <TextField label="Note" value={note} onChange={setNote} />
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={run}>Escalate</button>
        </div>
      </FoDialog>
    </div>
  );
}
